import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CurrentStopBanner,
  OrderItemsTable,
  DeliveryConfirmation,
  ReportFailure,
} from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  CurrentStop,
  OrderItem,
  failureReasons,
} from './driverActiveDeliveriesTypes';
import { useDeliveriesStore } from '../../../store/deliveriesStore';
import { getOrder } from '../../../services/ordersApi';
import styles from './DriverActiveDeliveries.module.css';

const DriverActiveDeliveries: React.FC = () => {
  const navigate = useNavigate();
  const stops = useDeliveriesStore((s) => s.stops);
  const loading = useDeliveriesStore((s) => s.loading);
  const error = useDeliveriesStore((s) => s.error);
  const saving = useDeliveriesStore((s) => s.saving);
  const fetchMyStops = useDeliveriesStore((s) => s.fetchMyStops);
  const deliver = useDeliveriesStore((s) => s.deliver);
  const fail = useDeliveriesStore((s) => s.fail);

  const [items, setItems] = useState<OrderItem[]>([]);
  const [selectedStopId, setSelectedStopId] = useState<number | null>(null);

  useEffect(() => { fetchMyStops(); }, [fetchMyStops]);

  // Every order that still needs delivery — independent of route order/optimization.
  // (Route sequencing/optimization lives on the My Route page.)
  const pendingStops = useMemo(
    () =>
      stops
        .filter((s) => s.status === 'pending' || s.status === 'in_transit')
        .sort((a, b) => a.sequence - b.sequence),
    [stops]
  );

  // The stop currently open for confirmation: the explicitly selected one,
  // falling back to the first pending stop.
  const selectedStop = useMemo(
    () => pendingStops.find((s) => s.id === selectedStopId) ?? pendingStops[0] ?? null,
    [pendingStops, selectedStopId]
  );

  // Lazy-load order items for the selected stop
  useEffect(() => {
    let cancelled = false;
    if (!selectedStop) { setItems([]); return; }
    getOrder(selectedStop.order_id)
      .then((order) => {
        if (cancelled) return;
        setItems(
          (order.items ?? []).map((it, i) => ({
            id: String(it.id ?? i),
            name: it.product_name || `SKU ${it.sku_id}`,
            quantity: `${it.quantity} ${it.quantity === 1 ? 'Case' : 'Cases'}`,
          }))
        );
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [selectedStop]);

  const currentStop: CurrentStop | null = selectedStop
    ? {
        number: selectedStop.sequence,
        name: selectedStop.customer_name || `Order ${selectedStop.order_id}`,
        message: 'Complete delivery for this stop. Other pending stops stay in the list above.',
        stopId: selectedStop.id,
      }
    : null;

  // Best-effort GPS capture at the moment of confirm/fail (VP-DRV-004). Never
  // blocks the action — geolocation may be unsupported, denied, or slow to
  // resolve (e.g. this environment's headless browser never resolves it at
  // all), and pod_latitude/pod_longitude are nullable columns server-side.
  const captureGps = (): Promise<{ pod_latitude?: number; pod_longitude?: number }> =>
    new Promise((resolve) => {
      if (!('geolocation' in navigator)) { resolve({}); return; }
      const timer = setTimeout(() => resolve({}), 5000);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timer);
          resolve({ pod_latitude: pos.coords.latitude, pod_longitude: pos.coords.longitude });
        },
        () => { clearTimeout(timer); resolve({}); },
        { enableHighAccuracy: false, timeout: 4500, maximumAge: 60000 }
      );
    });

  const handleDeliver = async (data: { quantity?: string; notes?: string; pod_photo_url: string; pod_signature_url: string; pod_received_by: string }) => {
    if (!selectedStop) return;
    // Encode quantity + notes together since the backend has only `notes` for
    // free-text POD; quantity is reported as part of the notes string.
    const combinedNotes = [
      data.quantity ? `Delivered quantity: ${data.quantity}` : '',
      data.notes ?? '',
    ].filter(Boolean).join('\n');
    const gps = await captureGps();
    await deliver(selectedStop.id, {
      pod_photo_url: data.pod_photo_url,
      pod_signature_url: data.pod_signature_url,
      pod_received_by: data.pod_received_by,
      notes: combinedNotes || undefined,
      ...gps,
    });
    setSelectedStopId(null); // fall back to next pending stop
    await fetchMyStops();
  };

  const handleFail = async ({ reasonId, notes }: { reasonId: string; notes?: string }) => {
    if (!selectedStop) return;
    const reason = failureReasons.find((r) => r.id === reasonId);
    const gps = await captureGps();
    await fail(selectedStop.id, {
      failure_reason: reason?.id || reasonId,
      failure_notes: notes,
      ...gps,
    });
    setSelectedStopId(null);
    await fetchMyStops();
  };

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && stops.length === 0}
        error={error}
        empty={!loading && pendingStops.length === 0}
        emptyMessage="No active deliveries. All orders delivered."
        onRetry={() => fetchMyStops()}
      >
        {pendingStops.length > 0 && (
          <>
            <p className={styles.listHeader}>
              {pendingStops.length} {pendingStops.length === 1 ? 'order' : 'orders'} to deliver
            </p>
            <div className={styles.stopList}>
              {pendingStops.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`${styles.stopItem} ${selectedStop?.id === s.id ? styles.stopItemActive : ''}`}
                  onClick={() => setSelectedStopId(s.id)}
                >
                  <span className={styles.stopSeq}>{s.sequence}</span>
                  <span className={styles.stopBody}>
                    <span className={styles.stopName}>{s.customer_name || `Order ${s.order_id}`}</span>
                    <span className={styles.stopMeta}>
                      Order #{s.order_id}
                      {s.customer_address ? ` · ${s.customer_address}` : ''}
                    </span>
                  </span>
                  <span className={styles.stopBadge}>{s.status.replace('_', ' ')}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {currentStop && selectedStop && (
          <>
            <CurrentStopBanner stop={currentStop} />

            <div className={styles.detailsCard}>
              <h3 className={styles.detailsTitle}>Order Details</h3>
              <div className={styles.detailsGrid}>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Order</span>
                  <span className={styles.detailsValue}>#{selectedStop.order_id}</span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Status</span>
                  <span className={styles.detailsValue}>{selectedStop.status.replace('_', ' ')}</span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Customer</span>
                  <span className={styles.detailsValue}>{selectedStop.customer_name || '—'}</span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Phone</span>
                  <span className={styles.detailsValue}>{selectedStop.customer_phone || '—'}</span>
                </div>
                <div className={`${styles.detailsItem} ${styles.full}`}>
                  <span className={styles.detailsLabel}>Address</span>
                  <span className={styles.detailsValue}>{selectedStop.customer_address || '—'}</span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Items</span>
                  <span className={styles.detailsValue}>{items.length}</span>
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Order Total</span>
                  <span className={styles.detailsValue}>${Number(selectedStop.order_total ?? 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <OrderItemsTable items={items} />
            <DeliveryConfirmation onConfirm={handleDeliver} onCancel={() => navigate('/routes')} />
            <ReportFailure reasons={failureReasons} onSubmit={handleFail} onCancel={() => navigate('/routes')} submitting={saving} />
          </>
        )}
      </AsyncBoundary>
    </div>
  );
};

export const DriverActiveDeliveriesPage: React.FC = () => <DriverActiveDeliveries />;

export { DriverActiveDeliveries };
export default DriverActiveDeliveries;
